'use client';

import { Configure } from 'react-instantsearch';
import { InstantSearchNext } from 'react-instantsearch-nextjs';

import { ALGOLIA_INDEX_NAME, searchClient } from '@/lib/algolia/searchClient';
import ProductCategoryListHeader from './components/ProductCategoryListHeader';
import ProductListInstantSearch from './components/ProductListInstantSearch';

import { SearchProps } from '@/lib/algolia/createCategorySearchProps';
import { CategoryRefinement } from '@/components/search/instantSearch/CategoryRefinement';
import { createURL } from '@/components/layout/Header/search/createUrl';
import { parseURL } from '@/components/layout/Header/search/parseUrl';

// Attributes to exclude in "CurrentRefinements" and "ClearRefinements"
export const excludedRefinementAttributes = [
  'query',
  'categories.lvl0',
  'categories.lvl1',
  'categories.lvl2',
  'categorySlugs.lvl0',
  'categorySlugs.lvl1',
  'categorySlugs.lvl2',
  'articleGroup.lvl0',
  'articleGroup.lvl1',
  'articleGroup.lvl2',
  'articleGroupSlugs.lvl0',
  'articleGroupSlugs.lvl1',
  'articleGroupSlugs.lvl2',
];

export const Search = ({
  serverUrl,
  facetFilters,
  categoryType,
  categorySlugsRootPath,
  articleGroupSlugsRootPath,
  showCategories,
  contentBlocks,
  relatedArticles,
  hideFacets,
  sanityProductCategoryEnrichment,
  filters,
}: SearchProps) => {
  let slugAttributes = [
    'categorySlugs.lvl0',
    'categorySlugs.lvl1',
    'categorySlugs.lvl2',
  ];

  let slugRootPath = categorySlugsRootPath;

  // For /produkter
  if (categoryType === 'articleGroup') {
    slugAttributes = [
      'articleGroupSlugs.lvl0',
      'articleGroupSlugs.lvl1',
      'articleGroupSlugs.lvl2',
    ];
    slugRootPath = articleGroupSlugsRootPath;
  }

  const hierarchicalFacetsRefinements: Record<string, string[]> = {};

  if (slugRootPath) {
    hierarchicalFacetsRefinements[slugAttributes[0]] = [slugRootPath];
  }

  return (
    <>
      <InstantSearchNext
        searchClient={searchClient}
        indexName={ALGOLIA_INDEX_NAME}
        insights
        routing={{
          router: {
            cleanUrlOnDispose: false,
            createURL,
            parseURL: parseURL(ALGOLIA_INDEX_NAME),
          },
        }}
        future={{
          preserveSharedStateOnUnmount: false,
        }}
      >
        <Configure
          facetFilters={facetFilters || undefined}
          filters={filters || undefined}
          hitsPerPage={32}
          clickAnalytics
          restrictHighlightAndSnippetArrays
          attributesToHighlight={[]}
          attributesToRetrieve={[
            'objectID',
            'posterName',
            'posterDescription',
            'supplier',
            'parentId',
            'price',
            'memberPrice',
            'campaignPrice',
            'sortprice',
            'image',
            'images',
            'surfaceTreat',
            'serie',
            'concept',
            'length',
            'width',
            'depth',
            'depth2',
            'height',
            'diameter',
            'space',
            'bedsize',
            'material',
            'colour',
            'clothing',
            'model',
            'colour_variants',
            'tags',
            'stock',
            'categories',
            'categorySlugs',
            'articleGroups',
            'articleGroupSlugs',
            'stockUpdated',
            'updated',
            'historicPath',
            'storesWithStockOrDisplay',
            'deliveryTimeText',
          ]}
        />

        {showCategories && (
          <CategoryRefinement
            attributes={slugAttributes}
            rootPath={slugRootPath || undefined}
            className="mb-8 hidden"
          />
        )}

        {hierarchicalFacetsRefinements && (
          <Configure
            hierarchicalFacetsRefinements={hierarchicalFacetsRefinements}
          />
        )}

        <ProductCategoryListHeader hideFacets={hideFacets} />
        <ProductListInstantSearch
          infiniteScroll
          contentBlocks={contentBlocks}
          relatedArticles={relatedArticles}
          slugRootPath={slugRootPath}
          sanityProductCategoryEnrichment={sanityProductCategoryEnrichment}
        />
      </InstantSearchNext>
    </>
  );
};

export default Search;
